import { ChangeDetectionStrategy, Component, input, output, ViewChild } from '@angular/core';
import { Animation, createAnimation, IonModal } from '@ionic/angular/standalone';
import { IconButtonComponent } from 'src/app/ui-kit/icon-button/icon-button.component';

/** Slide the modal wrapper in from the right and fade in the backdrop. */
function slideInFromRightEnter(baseEl: HTMLElement): Animation {
  const root = baseEl.shadowRoot!;
  const backdrop = root.querySelector('ion-backdrop')!;
  const wrapper = root.querySelector('.modal-wrapper')!;

  const backdropAnim = createAnimation().addElement(backdrop).fromTo('opacity', '0.01', 'var(--backdrop-opacity, 0.4)');

  const wrapperAnim = createAnimation().addElement(wrapper).fromTo('transform', 'translateX(100%)', 'translateX(0)');

  return createAnimation()
    .addElement(baseEl)
    .easing('cubic-bezier(0.4, 0, 0.2, 1)')
    .duration(300)
    .addAnimation([backdropAnim, wrapperAnim]);
}

/** Reverse of slideInFromRightEnter. */
function slideOutToRightLeave(baseEl: HTMLElement): Animation {
  return slideInFromRightEnter(baseEl).direction('reverse');
}

/**
 * Reusable side-panel modal that slides in from the right edge of the viewport
 * with a full-viewport backdrop. Delivered via `ion-modal` so Ionic teleports
 * the panel to `document.body` — that escapes every containing block created
 * by the host page (ion-tabs, transforms, etc.).
 *
 * Consumers drive the open/close state with a `[isOpen]` binding and react to
 * dismiss (backdrop tap, close-button click, programmatic close) via the
 * `(onDismiss)` output.
 *
 * @example
 * ```html
 * <app-side-panel-modal [isOpen]="panelOpen()" title="Filters" (onDismiss)="panelOpen.set(false)">
 *   <p>Projected body content…</p>
 * </app-side-panel-modal>
 * ```
 *
 * @feature shared
 */
@Component({
  selector: 'app-side-panel-modal',
  templateUrl: './side-panel-modal.component.html',
  styleUrls: ['./side-panel-modal.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'contents' },
  imports: [IonModal, IconButtonComponent],
})
export class SidePanelModalComponent {
  @ViewChild(IonModal) private ionModal?: IonModal;

  /** Heading text shown at the top of the panel. */
  readonly title = input<string>('');

  /** Whether the panel is open. Bind to a signal in the consumer. */
  readonly isOpen = input<boolean>(false);

  /** Whether to render the close button in the header. @default true */
  readonly showCloseButton = input<boolean>(true);

  /** Emits when the panel finishes dismissing (backdrop tap, close button, programmatic). */
  readonly onDismiss = output<void>();

  readonly enterAnimation = slideInFromRightEnter;
  readonly leaveAnimation = slideOutToRightLeave;

  /** Imperative close — used by the built-in close button. */
  close(): void {
    this.ionModal?.dismiss();
  }

  /** Called by `<ion-modal>` once its dismiss animation has finished. */
  handleDidDismiss(): void {
    this.onDismiss.emit();
  }
}
